import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, DatePicker, Table, Card, message, Typography, Space, Progress, Tag } from 'antd';
import { MinusCircleOutlined, PlusOutlined, UploadOutlined, SaveOutlined } from '@ant-design/icons';
import { API_USER_URL } from "@/env";
import dayjs from 'dayjs';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';

const { Title, Text } = Typography;

interface AccountFormData {
    username: string; // Used for fullname
    email: string;
    password: string;
    dob: dayjs.Dayjs;
    ref_code?: string;
    otp: string;
}

interface ProcessResult {
    index: number;
    status: 'pending' | 'processing' | 'success' | 'error';
    message: string;
    step: string;
    error?: any;
}

const CreateAccountsPage: React.FC = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState<Record<number, ProcessResult>>({});

    const breadcrumbs: BreadcrumbItem[] = [
        { label: 'Admin', path: '/admin' },
        { label: t('accounts.title'), path: '/admin/accounts' },
        { label: t('createAccounts.pageTitle') }
    ];

    // --- Isolated API Functions to prevent Admin logout ---

    const apiSignUp = async (data: { email: string; password: string; referral_code?: string }) => {
        const payload = {
            email: data.email,
            password: data.password,
            password_confirmation: data.password, // Assuming confirm is same
            ...(data.referral_code ? { referral_code: data.referral_code } : {})
        };

        const res = await fetch(`${API_USER_URL}/api/v1/be/sign-up`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const json = await res.json();
        if (!res.ok || !json.status) {
            const error: any = new Error(json.message || 'Signup failed');
            error.errors = json.errors || json.error;
            throw error;
        }
        return {
            token: json.data.token,
            user: json.data.user,
            error: json.data.error
        };
    };

    const apiUpdateProfile = async (token: string, userId: string | number, data: { fullname: string; birthday: string }) => {
        const res = await fetch(`${API_USER_URL}/api/v1/be/users/${userId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data),
        });
        const json = await res.json();
        if (!res.ok || (json.status === false)) {
            const error: any = new Error(json.message || 'Update profile failed');
            error.errors = json.errors || json.error;
            throw error;
        }
        return json.data;
    };

    const apiVerifyOtp = async (userId: string | number, otp: string) => {
        const res = await fetch(`${API_USER_URL}/api/v1/be/verify-email-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId, otp: otp }),
        });
        const json = await res.json();
        if (!res.ok || !json.status) {
            const error: any = new Error(json.message || 'OTP verification failed');
            error.errors = json.errors || json.error;
            throw error;
        }
        return true;
    };

    // --- Main Process Logic ---

    const processAccount = async (account: AccountFormData, index: number) => {
        try {
            // 1. Sign Up
            setResults(prev => ({
                ...prev,
                [index]: { index, status: 'processing', message: t('createAccounts.msgSigningUp'), step: t('createAccounts.stepSignup') }
            }));

            const signUpRes = await apiSignUp({
                email: account.email,
                password: account.password,
                referral_code: account.ref_code
            });

            // 2. Verify OTP
            setResults(prev => ({
                ...prev,
                [index]: { ...prev[index], message: t('createAccounts.msgVerifyingOtp'), step: t('createAccounts.stepVerify') }
            }));

            if (account.otp) {
                await apiVerifyOtp(signUpRes.user.id, account.otp);
            }

            // 3. Update Profile
            setResults(prev => ({
                ...prev,
                [index]: { ...prev[index], message: t('createAccounts.msgUpdatingProfile'), step: t('createAccounts.stepProfile') }
            }));

            const dobFormatted = account.dob ? account.dob.format('YYYY-MM-DD') : '';
            await apiUpdateProfile(signUpRes.token, signUpRes.user.id, {
                fullname: account.username,
                birthday: dobFormatted
            });

            // Success
            setResults(prev => ({
                ...prev,
                [index]: { index, status: 'success', message: t('createAccounts.msgCompleted'), step: t('createAccounts.stepDone') }
            }));

        } catch (error: any) {
            console.error("Batch processing error:", error);
            setResults(prev => ({
                ...prev,
                [index]: {
                    index,
                    status: 'error',
                    message: error.message || t('createAccounts.msgUnknownError'),
                    step: t('createAccounts.stepFailed'),
                    error: error
                }
            }));
        }
    };

    const onFinish = async (values: { users: AccountFormData[] }) => {
        if (!values.users || values.users.length === 0) {
            message.warning(t('createAccounts.pleaseAddOne'));
            return;
        }

        setIsProcessing(true);
        // Initialize results state
        const initialResults: Record<number, ProcessResult> = {};
        values.users.forEach((_, idx) => {
            initialResults[idx] = { index: idx, status: 'pending', message: t('createAccounts.pending'), step: '-' };
        });
        setResults(initialResults);

        // Process sequentially to be safe with rate limits, or map with Promise.all for speed
        // Let's do sequential for better feedback
        for (let i = 0; i < values.users.length; i++) {
            await processAccount(values.users[i], i);
        }

        setIsProcessing(false);
        message.info(t('createAccounts.batchComplete'));
    };

    return (
        <div className="p-6">
            <PageHeader title={t('createAccounts.pageTitle')} breadcrumbs={breadcrumbs} />

            <Card className="shadow-md mb-6">
                <div className="mb-4">
                    <Text type="secondary">
                        {t('createAccounts.description')}
                        <br />
                        <strong>{t('createAccounts.note')}</strong>
                    </Text>
                </div>

                <Form form={form} name="dynamic_form_nest_item" onFinish={onFinish} autoComplete="off" layout="vertical">
                    <Form.List name="users" initialValue={[{}]}>
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => {
                                    const result = results[index];
                                    let borderColor = undefined;
                                    if (result?.status === 'success') borderColor = '#52c41a';
                                    if (result?.status === 'error') borderColor = '#ff4d4f';
                                    if (result?.status === 'processing') borderColor = '#1890ff';

                                    return (
                                        <Card
                                            key={key}
                                            className="mb-4 bg-gray-50 border"
                                            style={{ borderColor: borderColor, borderWidth: result ? 2 : 1 }}
                                            size="small"
                                            title={<div className="flex flex-col gap-2">
                                                <Space>
                                                    <span>{t('createAccounts.accountLabel', { index: index + 1 })}</span>
                                                    {result && (
                                                        <Tag color={
                                                            result.status === 'success' ? 'success' :
                                                                result.status === 'error' ? 'error' :
                                                                    result.status === 'processing' ? 'processing' : 'default'
                                                        }>
                                                            {result.step}: {result.message}
                                                        </Tag>
                                                    )}
                                                </Space>
                                                {result?.status === 'error' && (
                                                    <div className="text-red-500 text-xs bg-red-50 p-2 rounded border border-red-100">
                                                        <strong>{t('createAccounts.detailsLabel')}:</strong> {result.error?.errors ? JSON.stringify(result.error.errors) : (result.error?.message || JSON.stringify(result.error))}
                                                    </div>
                                                )}
                                            </div>}
                                            extra={
                                                <MinusCircleOutlined onClick={() => remove(name)} className="text-red-500 hover:text-red-700 cursor-pointer text-lg" />
                                            }
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'username']}
                                                    label={t('createAccounts.fields.fullName')}
                                                    rules={[{ required: true, message: t('createAccounts.validation.missingFullName') }]}
                                                    className="mb-0"
                                                >
                                                    <Input placeholder={t('createAccounts.placeholders.fullName')} />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'email']}
                                                    label={t('createAccounts.fields.email')}
                                                    rules={[{ required: true, message: t('createAccounts.validation.missingEmail'), type: 'email' }]}
                                                    className="mb-0"
                                                >
                                                    <Input placeholder={t('createAccounts.placeholders.email')} />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'password']}
                                                    label={t('createAccounts.fields.password')}
                                                    initialValue="123456789"
                                                    rules={[{ required: true, message: t('createAccounts.validation.missingPassword') }]}
                                                    className="mb-0"
                                                >
                                                    <Input.Password placeholder={t('createAccounts.placeholders.password')} />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'dob']}
                                                    label={t('createAccounts.fields.dob')}
                                                    initialValue={dayjs(new Date())}
                                                    rules={[{ required: true, message: t('createAccounts.validation.missingDob') }]}
                                                    className="mb-0 w-full"
                                                >
                                                    <DatePicker className="w-full" format="YYYY-MM-DD" />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'otp']}
                                                    label={t('createAccounts.fields.otp')}
                                                    rules={[{ required: true, message: t('createAccounts.validation.missingOtp') }]}
                                                    initialValue={'123456'}
                                                    className="mb-0"
                                                >
                                                    <Input placeholder={t('createAccounts.placeholders.otp')} />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'ref_code']}
                                                    label={t('createAccounts.fields.referralCode')}
                                                    className="mb-0"
                                                >
                                                    <Input placeholder={t('createAccounts.placeholders.referralCode')} />
                                                </Form.Item>
                                            </div>
                                        </Card>
                                    )
                                })}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        {t('createAccounts.addRow')}
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={isProcessing} size="large" icon={<SaveOutlined />}>
                            {isProcessing ? t('createAccounts.processingBatch') : t('createAccounts.createAll')}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default CreateAccountsPage;
